/**
 * Billing Export Utility
 * Functions for generating and downloading Excel billing reports
 */

import * as XLSX from 'xlsx';

export interface BillingRow {
  matterCode: string;
  clientName: string;
  streamerName: string;
  videoCount: number;
  totalMinutes: number;
  billingRate: number;
  totalCost: number;
}

export interface StreamerBillingRow {
  streamerName: string;
  matterCode: string;
  clientName: string;
  videoCount: number;
  totalMinutes: number;
  totalCost: number;
}

export interface MatterSummaryRow {
  matterCode: string;
  clientName: string;
  billingRate: number;
  videoCount: number;
  totalMinutes: number;
  totalCost: number;
}

/**
 * Generate and download an Excel workbook from billing data
 */
export function generateBillingExcel(
  matterRows: MatterSummaryRow[],
  streamerRows: StreamerBillingRow[]
): void {
  const wb = XLSX.utils.book_new();

  // Sheet 1: Billing by Matter (aggregated)
  const matterData = matterRows.map((row) => ({
    'Matter Code': row.matterCode,
    'Client Name': row.clientName,
    'Billing Rate ($/min)': Number(row.billingRate.toFixed(2)),
    'Video Count': row.videoCount,
    'Total Minutes': Number(row.totalMinutes.toFixed(2)),
    'Total Cost ($)': Number(row.totalCost.toFixed(2)),
  }));

  const matterSheet = XLSX.utils.json_to_sheet(matterData);
  // Set column widths
  matterSheet['!cols'] = [
    { wch: 15 }, // Matter Code
    { wch: 25 }, // Client Name
    { wch: 18 }, // Billing Rate
    { wch: 12 }, // Video Count
    { wch: 14 }, // Total Minutes
    { wch: 14 }, // Total Cost
  ];
  XLSX.utils.book_append_sheet(wb, matterSheet, 'Billing by Matter');

  // Sheet 2: Billing by Creator
  const creatorData = streamerRows.map((row) => ({
    'Creator': row.streamerName,
    'Matter Code': row.matterCode,
    'Client Name': row.clientName,
    'Video Count': row.videoCount,
    'Total Minutes': Number(row.totalMinutes.toFixed(2)),
    'Total Cost ($)': Number(row.totalCost.toFixed(2)),
  }));

  const creatorSheet = XLSX.utils.json_to_sheet(creatorData);
  creatorSheet['!cols'] = [
    { wch: 25 }, // Creator
    { wch: 15 }, // Matter Code
    { wch: 25 }, // Client Name
    { wch: 12 }, // Video Count
    { wch: 14 }, // Total Minutes
    { wch: 14 }, // Total Cost
  ];
  XLSX.utils.book_append_sheet(wb, creatorSheet, 'Billing by Creator');

  // Generate filename and download
  const date = new Date().toISOString().split('T')[0];
  const filename = `echo-billing-report-${date}.xlsx`;
  XLSX.writeFile(wb, filename);
}

/**
 * Legacy CSV generation (kept for backwards compatibility)
 */
export function generateBillingCSV(
  rows: BillingRow[],
  streamerRows?: StreamerBillingRow[]
): string {
  const matterHeaders = [
    'Matter Code',
    'Client Name',
    'Streamer',
    'Video Count',
    'Total Minutes',
    'Billing Rate ($/min)',
    'Total Cost ($)',
  ];

  const csvRows = [
    '--- BILLING BY MATTER ---',
    matterHeaders.join(','),
    ...rows.map((row) =>
      [
        escapeCSV(row.matterCode),
        escapeCSV(row.clientName),
        escapeCSV(row.streamerName),
        row.videoCount.toString(),
        row.totalMinutes.toFixed(2),
        row.billingRate.toFixed(2),
        row.totalCost.toFixed(2),
      ].join(',')
    ),
  ];

  if (streamerRows && streamerRows.length > 0) {
    const streamerHeaders = [
      'Streamer',
      'Matter Code',
      'Client Name',
      'Video Count',
      'Total Minutes',
      'Total Cost ($)',
    ];

    csvRows.push('');
    csvRows.push('--- BILLING BY STREAMER ---');
    csvRows.push(streamerHeaders.join(','));
    streamerRows.forEach((row) => {
      csvRows.push(
        [
          escapeCSV(row.streamerName),
          escapeCSV(row.matterCode),
          escapeCSV(row.clientName),
          row.videoCount.toString(),
          row.totalMinutes.toFixed(2),
          row.totalCost.toFixed(2),
        ].join(',')
      );
    });
  }

  return csvRows.join('\n');
}

function escapeCSV(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function downloadCSV(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

export function generateBillingFilename(): string {
  const date = new Date().toISOString().split('T')[0];
  return `echo-billing-report-${date}.csv`;
}
